import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Form, Alert, InputGroup } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
    Rocket, 
    Heart, 
    Bell, 
    CircleUser, 
    AlertTriangle, 
    Mail, 
    Lock, 
    Eye, 
    EyeOff, 
    LogIn, 
    ArrowLeft 
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const LoginPage = () => {
    const { login, loginWithProvider, isAuthenticated, error, clearError } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });
    const [isLoading, setIsLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [localError, setLocalError] = useState(null); // Add local error state
    const [selectedRole, setSelectedRole] = useState('Job Seeker');

    console.log('LoginPage Render State - error:', error, 'localError:', localError);

    const handleSocialLogin = async (provider) => {
        setIsLoading(true);
        setLocalError(null);
        try {
            await loginWithProvider(provider, selectedRole);
        } catch (err) {
            console.error('Social login error:', err);
            setLocalError(err.message || 'OAuth authentication failed.');
            setIsLoading(false);
        }
    };

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated && !isLoading) {
            const from = location.state?.from?.pathname || '/dashboard';
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, isLoading, location.state?.from?.pathname, navigate]);

    // Clear errors when component mounts
    useEffect(() => {
        // Only clear errors on initial mount, not after every login attempt
        const hasInitiallyMounted = sessionStorage.getItem('loginPageMounted');
        if (!hasInitiallyMounted) {
            clearError();
            sessionStorage.setItem('loginPageMounted', 'true');
            // Clear any old persisted errors on fresh page load
            sessionStorage.removeItem('loginError');
        }
        
        // Load any persisted error from sessionStorage ONLY if it's recent
        const persistedError = sessionStorage.getItem('loginError');
        const errorTimestamp = sessionStorage.getItem('loginErrorTime');
        const now = Date.now();
        
        if (persistedError && errorTimestamp) {
            const errorAge = now - parseInt(errorTimestamp);
            // Only show error if it's less than 30 seconds old (recent login attempt)
            if (errorAge < 30000) {
                setLocalError(persistedError);
            } else {
                // Clear old errors
                sessionStorage.removeItem('loginError');
                sessionStorage.removeItem('loginErrorTime');
            }
        }
        
        // Clean up on unmount
        return () => {
            sessionStorage.removeItem('loginPageMounted');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Clear validation error for this field
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }
        
        if (!formData.password) {
            errors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            errors.password = 'Password must be at least 6 characters';
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!validateForm()) {
            return false;
        }
        
        setIsLoading(true);
        setLocalError(null);
        
        try {
            const result = await login({
                email: formData.email,
                password: formData.password,
                rememberMe: formData.rememberMe
            });
            
            console.log('LoginPage: login result =', result);
            if (result.success) {
                // Clear any persisted error before navigation
                sessionStorage.removeItem('loginError');
                const from = location.state?.from?.pathname || '/dashboard';
                navigate(from, { replace: true });
            } else {
                // Persist error in both local state AND sessionStorage with timestamp
                const errorMessage = result.error || 'Login failed. Please try again.';
                console.log('LoginPage: Setting localError to:', errorMessage);
                setLocalError(errorMessage);
                sessionStorage.setItem('loginError', errorMessage);
                sessionStorage.setItem('loginErrorTime', Date.now().toString());
            }
        } catch (error) {
            console.error('Login submission error:', error);
            const errorMessage = 'Login failed. Please try again.';
            setLocalError(errorMessage);
            sessionStorage.setItem('loginError', errorMessage);
            sessionStorage.setItem('loginErrorTime', Date.now().toString());
        } finally {
            setIsLoading(false);
        }
        
        return false;
    };

    return (
        <div className="auth-body">
            <div className="auth-container">
                <Row className="min-vh-100 g-0">
                    {/* Left Side - Branding */}
                    <Col lg={6} className="d-none d-lg-flex">
                        <div className="auth-brand-section">
                            <div className="auth-brand-overlay"></div>
                            <div className="auth-brand-content">
                                <div className="brand-logo-large">
                                    <h1>
                                        <span className="brand-name">Zpluse</span>{' '}
                                        <span className="brand-suffix">Jobs Finder</span>
                                    </h1>
                                    <p className="brand-tagline">
                                        Welcome back! Continue your journey to find amazing opportunities
                                    </p>
                                </div>
                                
                                <div className="auth-features">
                                    <div className="feature-item">
                                        <div className="feature-icon">
                                            <Rocket size={24} />
                                        </div>
                                        <div className="feature-text">
                                            <h6>Quick Access</h6>
                                            <p>Jump right back into your personalized job dashboard</p>
                                        </div>
                                    </div>
                                    <div className="feature-item">
                                        <div className="feature-icon">
                                            <Heart size={24} />
                                        </div>
                                        <div className="feature-text">
                                            <h6>Saved Jobs</h6>
                                            <p>Access your saved jobs and track your applications</p>
                                        </div>
                                    </div>
                                    <div className="feature-item">
                                        <div className="feature-icon">
                                            <Bell size={24} />
                                        </div>
                                        <div className="feature-text">
                                            <h6>Smart Alerts</h6>
                                            <p>Get notified about new opportunities that match your profile</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>
                    
                    {/* Right Side - Login Form */}
                    <Col lg={6}>
                        <div className="auth-form-section">
                            <div className="auth-form-container">
                                {/* Mobile Brand Logo */}
                                <div className="mobile-brand d-lg-none text-center mb-4">
                                    <h3>
                                        <span className="brand-name">Zpluse</span>{' '}
                                        <span className="brand-suffix">Jobs Finder</span>
                                    </h3>
                                </div>
                                
                                <div className="auth-form-header text-center">
                                    <div className="login-avatar mb-3">
                                        <CircleUser size={64} className="text-primary" />
                                    </div>
                                    <h2 className="auth-title">Welcome Back</h2>
                                    <p className="auth-subtitle">Sign in to your account to continue</p>
                                </div>
                                
                                {/* Global Error Alert */}
                                {(localError || error) && (
                                    <div className="alert alert-danger alert-custom alert-error d-flex align-items-center mb-3" role="alert">
                                        <AlertTriangle className="me-2" size={18} style={{ minWidth: '18px', display: 'inline-block' }} />
                                        <span>
                                            {typeof (localError || error) === 'string'
                                                ? (localError || error)
                                                : (localError || error)?.message || 'Invalid email or password. Please try again.'
                                            }
                                        </span>
                                    </div>
                                )}
                                
                                <Form onSubmit={handleSubmit} className="auth-form" noValidate>
                                    {/* Add debugging and additional prevention */}
                                    <input type="hidden" name="preventBrowserSubmit" value="true" />
                                    
                                    {/* Email Field */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email Address</Form.Label>
                                        <InputGroup>
                                            <span className="input-group-text">
                                                <Mail size={18} />
                                            </span>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                placeholder="Enter your email address"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                isInvalid={!!validationErrors.email}
                                                isValid={formData.email && !validationErrors.email && /\S+@\S+\.\S+/.test(formData.email)}
                                                className="modern-input"
                                                required
                                            />
                                        </InputGroup>
                                        {validationErrors.email && (
                                            <div className="invalid-feedback d-block">
                                                {validationErrors.email}
                                            </div>
                                        )}
                                    </Form.Group>
                                    
                                    {/* Password Field */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>Password</Form.Label>
                                        <InputGroup>
                                            <span className="input-group-text">
                                                <Lock size={18} />
                                            </span>
                                            <Form.Control
                                                type={showPassword ? 'text' : 'password'}
                                                name="password"
                                                placeholder="Enter your password"
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                isInvalid={!!validationErrors.password}
                                                isValid={formData.password && formData.password.length >= 6}
                                                className="modern-input"
                                                required
                                            />
                                            <Button 
                                                variant="outline-secondary" 
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="d-flex align-items-center"
                                            >
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </Button>
                                        </InputGroup>
                                        {validationErrors.password && (
                                            <div className="invalid-feedback d-block">
                                                {validationErrors.password}
                                            </div>
                                        )}
                                    </Form.Group>

                                    {/* Remember Me & Forgot Password Row */}
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <Form.Check
                                            type="checkbox"
                                            name="rememberMe"
                                            checked={formData.rememberMe}
                                            onChange={handleInputChange}
                                            label="Remember me"
                                            className="remember-me-check"
                                        />
                                        <Link to="/forgot-password" className="forgot-password-link">
                                            Forgot Password?
                                        </Link>
                                    </div>
                                    
                                    {/* Submit Button */}
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="auth-btn w-100 mb-4 d-flex align-items-center justify-content-center"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <div className="loading-spinner me-2" style={{ width: '1rem', height: '1rem' }}></div>
                                        ) : (
                                            <LogIn className="me-2" size={18} />
                                        )}
                                        {isLoading ? 'Signing In...' : 'Sign In'}
                                    </Button>

                                    {/* Social Logins */}
                                    <div className="social-auth-section mb-4">
                                        <div className="auth-divider mb-3">
                                            <span>Or continue with</span>
                                        </div>
                                        
                                        <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
                                            <span className="small text-muted">Role:</span>
                                            <Button
                                                type="button"
                                                variant={selectedRole === 'Job Seeker' ? 'primary' : 'outline-secondary'}
                                                size="sm"
                                                className="px-3"
                                                onClick={() => setSelectedRole('Job Seeker')}
                                                disabled={isLoading}
                                            >
                                                Job Seeker
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={selectedRole === 'Recruiter' ? 'primary' : 'outline-secondary'}
                                                size="sm"
                                                className="px-3"
                                                onClick={() => setSelectedRole('Recruiter')}
                                                disabled={isLoading}
                                            >
                                                Recruiter
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={selectedRole === 'Admin' ? 'primary' : 'outline-secondary'}
                                                size="sm"
                                                className="px-3"
                                                onClick={() => setSelectedRole('Admin')}
                                                disabled={isLoading}
                                            >
                                                Admin
                                            </Button>
                                        </div>

                                        <div className="d-flex gap-2">
                                            <Button
                                                type="button"
                                                variant="outline-secondary"
                                                className="w-100 d-flex align-items-center justify-content-center gap-2 py-2"
                                                onClick={() => handleSocialLogin('google')}
                                                disabled={isLoading}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" className="me-1">
                                                    <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v3.9h6.69c-.29 1.5-1.13 2.77-2.4 3.61l3.71 2.87c2.17-2 3.84-5 3.84-8.31z"/>
                                                    <path fill="#34A853" d="M12 24c3.24 0 5.97-1.08 7.96-2.91l-3.71-2.87c-1.07.72-2.45 1.15-4.12 1.15-3.17 0-5.85-2.14-6.81-5.02L1.31 15.22l-1.01.78C2.28 20.35 6.75 24 12 24z"/>
                                                    <path fill="#FBBC05" d="M5.19 14.25c-.24-.72-.38-1.49-.38-2.25s.14-1.53.38-2.25V4.73L1.31 1.76l-.78 1.01C-.8 6.02-.8 11.98 0 16.5l5.19-2.25z"/>
                                                    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.96 1.19 15.24 0 12 0 6.75 0 2.28 3.65.53 8.01l4.66 3.61c.96-2.88 3.64-5.02 6.81-5.02z"/>
                                                </svg>
                                                Google
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline-secondary"
                                                className="w-100 d-flex align-items-center justify-content-center gap-2 py-2"
                                                onClick={() => handleSocialLogin('linkedin_oidc')}
                                                disabled={isLoading}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="#0A66C2" className="me-1">
                                                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.779-1.75-1.75s.784-1.75 1.75-1.75 1.75.779 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                                                </svg>
                                                LinkedIn
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="auth-divider">
                                        <span>Don't have an account?</span>
                                    </div>
                                    
                                    <div className="auth-footer text-center">
                                        <p className="mb-0">
                                            <Link to="/register" className="register-link">
                                                Create Your Account
                                            </Link>
                                        </p>
                                    </div>
                                </Form>
                                
                                {/* Back to Home Link */}
                                <div className="text-center mt-4">
                                    <Link 
                                        to="/" 
                                        className="back-to-home-link d-inline-flex align-items-center"
                                    >
                                        <ArrowLeft className="me-2" size={18} />
                                        Back to Home
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default LoginPage;